import { Paper, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { prepFormValuesForUpsert } from 'src/non-financial-data/prepFormValuesForUpsert';
import { UpsertEntityInput } from 'src/data-capture/merge-review/useUpsertEntities';
import PageWithSidebar from '../page-nav/PageWithSidebar';
import { isFormEditable } from './dataHelpers';
import { getInitialFormData } from './dynamicUiInitialValuesHelpers';
import { DynamicUiProvider } from './DynamicUiProvider';
import DynamicLayoutItem from './layout/DynamicLayoutItem';
import { DynamicUiData, FormValuesType, UiSection } from './types';

type Props = {
  uiData: DynamicUiData;
  footer?: React.ReactNode;
  onSubmit: (clientId: string, upsertEntities: UpsertEntityInput[]) => Promise<void>;
};

export default function DynamicPage({ uiData, onSubmit, footer = null }: Props) {
  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    ...getInitialFormData(uiData, uiData.ui.layout),
  });
  const { clientId } = useCurrentClient();
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    const isDirty = form.isDirty();
    form.setValues(getInitialFormData(uiData, uiData.ui.layout).initialValues);

    // Preserve dirty state after overriding values
    if (!isDirty) {
      form.resetDirty();
    }
  }, [uiData]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (form.isDirty()) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSubmit = async (formValues: FormValuesType) => {
    if (!uiData) {
      return;
    }
    const valuesToSubmit = prepFormValuesForUpsert(formValues, uiData, form, false);
    await onSubmit(clientId, valuesToSubmit);
    form.resetDirty();
    setIsEditing(false);
  };

  const { tab, sectionSlug } = useParams();
  const sidebarLinks =
    uiData?.ui.layout
      .filter((item): item is UiSection => item.type === 'UI_ENTITY')
      .map((item, index) => {
        const section = slugify(item.label || '');
        const href = `/client/${clientId}/${tab}/${section}`;
        return {
          label: item.label || `Section ${index + 1}`,
          section,
          href,
        };
      }) || [];

  const sections = uiData?.ui.layout.filter((item): item is UiSection => item.type === 'UI_ENTITY');
  const currentSection = sections?.find(item => slugify(item.label) === sectionSlug);
  const toggleEditing = () => {
    setIsEditing(!isEditing);
    form.reset();
  };
  const isEditable = currentSection ? isFormEditable(form, currentSection, uiData) : false;

  const navigationPath = resolveNavigationPathForTab(clientId, tab || '', sections, sectionSlug);
  const location = useLocation();
  if (navigationPath !== location.pathname) {
    return <Navigate to={navigationPath} />;
  }

  if (!currentSection) {
    return null;
  }

  return (
    <form role="form" onSubmit={form.onSubmit(handleSubmit)}>
      <PageWithSidebar links={sidebarLinks} activeUrl={window.location.pathname}>
        <DynamicUiProvider
          form={form}
          uiData={uiData}
          currentSection={currentSection}
          isEditable={isEditable}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          toggleEditing={toggleEditing}
          isDeletable={false}
          isCreateMode={false}
          clientId={clientId}
        >
          <Stack>
            <Paper variant="content" mb="md">
              {currentSection && <DynamicLayoutItem item={currentSection} />}
            </Paper>
            {footer}
          </Stack>
        </DynamicUiProvider>
      </PageWithSidebar>
    </form>
  );
}

const slugify = (text: string) => {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '-');
};

const resolveNavigationPathForTab = (
  clientId: string,
  tab: string,
  sections: UiSection[] | undefined,
  sectionSlug: string | undefined,
): string => {
  const tabPath = `/client/${clientId}/${tab}`;
  if (!sectionSlug) {
    if (sections && sections.length > 0) {
      const path = `${tabPath}/${slugify(sections[0].label)}`;
      return path;
    }
    return tabPath;
  }

  const section = sections?.find(item => slugify(item.label) === sectionSlug);
  if (!section) {
    if (sections && sections.length > 0) {
      const path = `${tabPath}/${slugify(sections[0].label)}`;
      return path;
    }
    return tabPath;
  }

  return `${tabPath}/${sectionSlug}`;
};
